import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, InputGroup } from 'react-bootstrap';
import { apiService } from '../services/api';

interface PasswordResetModalProps {
  show: boolean;
  onHide: () => void;
  domain: string;
  onPasswordReset: () => void;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  show,
  onHide,
  domain,
  onPasswordReset,
}) => {
  const [users, setUsers] = useState<Array<{ id: number; email: string }>>([]);
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (show && domain) {
      loadUsers();
    }
  }, [show, domain]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getUsersByDomain(domain);
      setUsers(response.users || []);
      if (response.users && response.users.length > 0) {
        setSelectedEmail(response.users[0].email);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kullanıcılar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const generateStrongPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';

    // Ensure at least one of each type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)];

    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');

    setPassword(password);
    setShowPassword(true);
  };

  const handleResetPassword = async () => {
    if (!selectedEmail || !password) {
      setError('Lütfen email ve şifre alanlarını doldurun');
      return;
    }

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await apiService.resetPassword(domain, selectedEmail, password);

      setSuccess('Şifre başarıyla sıfırlandı!');
      setTimeout(() => {
        onPasswordReset();
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Şifre sıfırlanırken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedEmail('');
    setPassword('');
    setShowPassword(false);
    setError(null);
    setSuccess(null);
    onHide();
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(password);
    setSuccess('Şifre panoya kopyalandı!');
    setTimeout(() => setSuccess(null), 2000);
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-key"></i> Şifre Sıfırla
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <div className="mb-3">
          <strong>Domain:</strong> {domain}
        </div>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Kullanıcı Seçin</Form.Label>
            <Form.Select
              value={selectedEmail}
              onChange={(e) => setSelectedEmail(e.target.value)}
              disabled={loading || users.length === 0}
            >
              {users.length === 0 && <option>Kullanıcı bulunamadı</option>}
              {users.map((user) => (
                <option key={user.id} value={user.email}>
                  {user.email}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Yeni Şifre</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Şifre girin veya oluştur"
                disabled={loading}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Şifreyi Gizle' : 'Şifreyi Göster'}
              >
                <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
              </Button>
              {password && (
                <Button
                  variant="outline-secondary"
                  onClick={copyToClipboard}
                  title="Panoya Kopyala"
                >
                  <i className="bi bi-clipboard"></i>
                </Button>
              )}
            </InputGroup>
            <Form.Text className="text-muted">
              Şifre en az 6 karakter olmalıdır
            </Form.Text>
          </Form.Group>

          <div className="d-grid gap-2 mb-3">
            <Button
              variant="info"
              onClick={generateStrongPassword}
              disabled={loading}
            >
              <i className="bi bi-shield-lock"></i> Güçlü Şifre Oluştur
            </Button>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          İptal
        </Button>
        <Button
          variant="primary"
          onClick={handleResetPassword}
          disabled={loading || !selectedEmail || !password}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Sıfırlanıyor...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle"></i> Şifreyi Sıfırla
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PasswordResetModal;
